import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import type { AssetVerification } from '../lib/verifications';
import { Badge, Card, CardHeader, EmptyState, Spinner } from './ui';

/** Every time a device was checked off in a room during a verification sweep. */
export function VerificationHistoryCard({ assetId }: { assetId: string }) {
  const query = useQuery({
    queryKey: ['asset-verifications', assetId],
    queryFn: () => api.get<{ items: AssetVerification[] }>(`/assets/${assetId}/verifications`),
    select: (data) => data.items,
  });

  return (
    <Card>
      <CardHeader title="Verifications" description="Each time someone checked this device off where it’s kept" />
      {query.isLoading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : !query.data?.length ? (
        <EmptyState icon={<ClipboardCheck />} title="Never verified" description="Devices are verified from Inventory → Verify assets." />
      ) : (
        <ol className="space-y-4 p-5">
          {query.data.map((verification) => (
            <li key={verification.id} className="relative pl-5 text-sm">
              <span className="absolute top-1.5 left-0 size-2 rounded-full bg-emerald-500" />
              <p className="flex flex-wrap items-center gap-2 text-slate-800">
                {verification.room ? (
                  <Link to={`/locations/rooms/${verification.room.id}`} className="font-medium hover:text-brand-700">
                    {verification.room.name}
                    {verification.building && ` · ${verification.building.name}`}
                  </Link>
                ) : (
                  <span className="font-medium">{verification.building?.name ?? 'No location'}</span>
                )}
                {verification.moved && <Badge tone="amber">Moved here</Badge>}
                {verification.cartName && <Badge>On {verification.cartName}</Badge>}
              </p>
              <p className="text-xs text-slate-500">
                {formatDateTime(verification.verifiedAt)} · {verification.verifiedByName}
                {verification.moved && verification.previousLabel && ` · was kept in ${verification.previousLabel}`}
              </p>
              {verification.notes && <p className="mt-0.5 text-xs text-slate-600">{verification.notes}</p>}
              {verification.workOrder && (
                <p className="mt-0.5 text-xs">
                  <Link to={`/work-orders/${verification.workOrder.id}`} className="text-brand-700 hover:underline">
                    #{verification.workOrder.number}
                  </Link>{' '}
                  opened to check this room again
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
